import type { FormSavePayload } from "../types/formBuilder";
import type { FormDto } from "../types/form";
import type { FormsListParams, FormsListResponse } from "../types/formList";
import type { SectionDto } from "../types/section";
import type {
  SubmissionsExportColumns,
  SubmissionsExportParams,
  SubmissionsListParams,
  SubmissionsListResponse,
} from "../types/submission";
import type { FormSubmitPayload, FormSubmitResponse } from "../types/submit";
import type { QuizCheckPayload, QuizCheckResult } from "../types/quiz";
import { parseQuizCheckResult } from "../lib/forms/quiz";
import {
  FORM_FIELD_TYPES,
  type ApiFieldTypeOption,
} from "../lib/formBuilder/apiFieldTypes";
import { normalizeSubmissionsList } from "../lib/forms/normalizeSubmission";
import { normalizeSubmissionsExportColumns } from "../lib/forms/normalizeExportColumns";
import { buildSubmissionsExportQuery } from "../lib/forms/submissionsExport";
import { normalizeHookLogsList } from "../lib/forms/normalizeHookLog";
import type {
  HookLogDto,
  HookLogsListParams,
  HookLogsListResponse,
} from "../types/hookLog";
import { apiDownload, apiRequest } from "./client";
import { downloadBlob } from "../lib/http/downloadBlob";
import {
  normalizeSectionsResponse,
  unwrapFormDto,
} from "../lib/forms/resolveFormSections";
import {
  normalizeRemoteChoices,
  type RemoteChoicesResult,
} from "../lib/forms/remoteChoices";
import {
  normalizeYandexSyncResponse,
  type YandexSyncResponse,
} from "../lib/forms/normalizeYandexSync";

/** Доступные типы полей (зашиты на бэкенде, без отдельного роута). */
export function fetchFormFieldTypes(): Promise<ApiFieldTypeOption[]> {
  return Promise.resolve(FORM_FIELD_TYPES);
}

/** Список форм — только с токеном (`FormResource`, camelCase). */
export function fetchFormsList(
  params: FormsListParams = {},
): Promise<FormsListResponse> {
  const page = params.page ?? 1;
  const count = params.count ?? 15;
  const query = new URLSearchParams({
    page: String(page),
    count: String(count),
  });

  if (params.sort) query.set("sort", params.sort);
  if (params.direction) query.set("direction", params.direction);
  if (params.source) query.set("source[eq]", params.source);
  if (params.organisationId != null) {
    query.set("organisationId", String(params.organisationId));
  }
  if (params.syncYandex) query.set("syncYandex", "1");

  return apiRequest<FormsListResponse>(`/api/v1/forms?${query}`);
}

/** POST /api/v1/forms/yandex/sync — только global manager. */
export function syncYandexForms(): Promise<YandexSyncResponse> {
  return apiRequest<unknown>("/api/v1/forms/yandex/sync", {
    method: "POST",
  }).then(normalizeYandexSyncResponse);
}

/** Схема формы с токеном (submitHooks и т.д.) — для админки. */
export function fetchForm(
  formId: number | string,
  options: { auth?: boolean } = {},
): Promise<FormDto> {
  const { auth = true } = options;
  return apiRequest<unknown>(
    `/api/v1/forms/${encodeURIComponent(String(formId))}`,
    { auth },
  ).then(unwrapFormDto);
}

/** Разделы с полями (только с токеном). */
export function fetchFormSections(
  formId: number,
  options: { auth?: boolean } = {},
): Promise<SectionDto[]> {
  const { auth = true } = options;
  return apiRequest<unknown>(`/api/v1/forms/${formId}/sections`, {
    auth,
  }).then(normalizeSectionsResponse);
}

export function createForm(payload: FormSavePayload): Promise<FormDto> {
  return apiRequest<FormDto>(`/api/v1/forms`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateForm(
  formId: number,
  payload: FormSavePayload,
): Promise<FormDto> {
  return apiRequest<FormDto>(`/api/v1/forms/${formId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function replaceForm(
  formId: number,
  payload: FormSavePayload,
): Promise<FormDto> {
  return apiRequest<FormDto>(`/api/v1/forms/${formId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function updateFormStatus(
  formId: number,
  payload: { isClosed: boolean; closedMessage?: string | null },
): Promise<FormDto> {
  return apiRequest<FormDto>(`/api/v1/forms/${formId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function updateFormOrganisation(
  formId: number,
  organisationId: number | null,
): Promise<FormDto> {
  return apiRequest<FormDto>(`/api/v1/forms/${formId}`, {
    method: "PATCH",
    body: JSON.stringify({ organisationId }),
  });
}

export function deleteForm(formId: number): Promise<void> {
  return apiRequest<void>(`/api/v1/forms/${formId}`, {
    method: "DELETE",
  });
}

/** Удаление сохранённого поля формы. */
export function deleteFormField(
  formId: number,
  fieldId: number,
): Promise<void> {
  return apiRequest<void>(`/api/v1/forms/${formId}/fields/${fieldId}`, {
    method: "DELETE",
  });
}

/** Удаление сохранённого раздела формы. */
export function deleteFormSection(
  formId: number,
  sectionId: number,
): Promise<void> {
  return apiRequest<void>(`/api/v1/forms/${formId}/sections/${sectionId}`, {
    method: "DELETE",
  });
}

/** GET /api/v1/forms/{id}/fields/{fieldId}/choices?source= */
export function fetchFieldChoices(
  formId: number | string,
  fieldId: number,
  source: string,
  options: { auth?: boolean } = {},
): Promise<RemoteChoicesResult> {
  const query = new URLSearchParams({ source });
  return apiRequest<unknown>(
    `/api/v1/forms/${encodeURIComponent(String(formId))}/fields/${fieldId}/choices?${query}`,
    {
      auth: options.auth ?? false,
      skipUnauthorizedHandler: true,
    },
  ).then(normalizeRemoteChoices);
}

/** POST /api/v1/forms/preview-choices — превью внешнего списка (как generate-fields). */
export function previewChoices(payload: {
  url: string;
  path?: string;
  item?: string;
  value?: string;
  source?: string;
}): Promise<RemoteChoicesResult> {
  const body: Record<string, string> = { url: payload.url.trim() };
  if (payload.path?.trim()) body.path = payload.path.trim();
  if (payload.item?.trim()) body.item = payload.item.trim();
  if (payload.value?.trim()) body.value = payload.value.trim();
  if (payload.source?.trim()) body.source = payload.source.trim();

  return apiRequest<unknown>("/api/v1/forms/preview-choices", {
    method: "POST",
    body: JSON.stringify(body),
  }).then(normalizeRemoteChoices);
}

export function submitForm(
  formId: number | string,
  payload: FormSubmitPayload,
): Promise<FormSubmitResponse> {
  return apiRequest<FormSubmitResponse>(
    `/api/v1/forms/${encodeURIComponent(String(formId))}/submit`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      auth: false,
    },
  );
}

/** POST /api/v1/forms/{id}/quiz/check */
export function checkQuizAnswer(
  formId: number | string,
  payload: QuizCheckPayload,
): Promise<QuizCheckResult> {
  return apiRequest<unknown>(
    `/api/v1/forms/${encodeURIComponent(String(formId))}/quiz/check`,
    {
      method: "POST",
      body: JSON.stringify(payload),
      auth: false,
    },
  ).then((body) => {
    const result = parseQuizCheckResult(body);
    if (!result) {
      throw new Error("Не удалось проверить ответ");
    }
    return result;
  });
}

/** Ответы на форму — scope manager/admin. */
export function fetchFormSubmissions(
  formId: number,
  params: SubmissionsListParams = {},
): Promise<SubmissionsListResponse> {
  const page = params.page ?? 1;
  const count = params.count ?? 15;
  const query = new URLSearchParams({
    page: String(page),
    count: String(count),
  });

  if (params.nextId) {
    query.delete("page");
    query.set("nextId", params.nextId);
  }

  return apiRequest<unknown>(
    `/api/v1/forms/${formId}/submissions?${query}`,
  ).then((body) => {
    const data = normalizeSubmissionsList(body);
    const record =
      body && typeof body === "object"
        ? (body as SubmissionsListResponse & { next_id?: string | null })
        : null;
    const meta = record?.meta ?? undefined;
    const nextId =
      typeof record?.nextId === "string"
        ? record.nextId
        : typeof record?.next_id === "string"
          ? record.next_id
          : (record?.nextId ?? record?.next_id ?? undefined);

    return { data, meta, links: undefined, nextId: nextId ?? null };
  });
}

/** Количество ответов (meta.total) без загрузки всех страниц. */
export async function fetchFormSubmissionCount(
  formId: number,
): Promise<number> {
  const response = await fetchFormSubmissions(formId, { page: 1, count: 1 });
  const total = response.meta?.total;
  if (typeof total === "number" && Number.isFinite(total)) {
    return total;
  }
  return response.data?.length ?? 0;
}

export function deleteFormSubmission(
  formId: number,
  submissionId: number,
): Promise<void> {
  return apiRequest<void>(
    `/api/v1/forms/${formId}/submissions/${submissionId}`,
    {
      method: "DELETE",
    },
  );
}

/** Список столбцов для экспорта ответов (служебные + поля формы). */
export function fetchSubmissionsExportColumns(
  formId: number,
): Promise<SubmissionsExportColumns> {
  return apiRequest<unknown>(
    `/api/v1/forms/${formId}/submissions/export/columns`,
  ).then((body) => normalizeSubmissionsExportColumns(body));
}

/** Экспорт ответов формы в CSV, XLSX или DOCX. */
export async function exportFormSubmissions(
  formId: number,
  params: SubmissionsExportParams = {},
): Promise<void> {
  const query = buildSubmissionsExportQuery(params);
  const { blob, filename } = await apiDownload(
    `/api/v1/forms/${formId}/submissions/export${query}`,
  );
  downloadBlob(blob, filename);
}

/** Журнал отправок вебхуков для ответа. */
export function fetchSubmissionHookLogs(
  formId: number,
  submissionId: number,
): Promise<HookLogDto[]> {
  return apiRequest<unknown>(
    `/api/v1/forms/${formId}/submissions/${submissionId}/hook-logs`,
  ).then((body) => normalizeHookLogsList(body));
}

/** Журнал вебхуков формы (пагинированный список). */
export function fetchFormHookLogs(
  formId: number,
  params: HookLogsListParams = {},
): Promise<HookLogsListResponse> {
  const page = params.page ?? 1;
  const count = params.count ?? 15;
  const query = new URLSearchParams({
    page: String(page),
    count: String(count),
  });

  if (params.submissionId != null) {
    query.set("submission_id", String(params.submissionId));
  }

  return apiRequest<unknown>(`/api/v1/forms/${formId}/hook-logs?${query}`).then(
    (body) => {
      const data = normalizeHookLogsList(body);
      const meta =
        body && typeof body === "object" && "meta" in body
          ? ((body as HookLogsListResponse).meta ?? undefined)
          : undefined;
      const links =
        body && typeof body === "object" && "links" in body
          ? ((body as HookLogsListResponse).links ?? undefined)
          : undefined;

      return { data, meta, links };
    },
  );
}

/** Повторить отправку вебхуков для ответа. */
export function retrySubmissionHooks(
  formId: number,
  submissionId: number,
): Promise<void> {
  return apiRequest<void>(
    `/api/v1/forms/${formId}/submissions/${submissionId}/retry-hooks`,
    { method: "POST" },
  );
}

export {
  loadPublicForm as fetchPublicForm,
  loadPublicFormSchema as fetchPublicFormSchema,
} from "../lib/forms/publicForm";
export { loadFormForEdit as fetchFormForEdit } from "../lib/formBuilder/loadFormForEdit";
export { duplicateFormFromSource as duplicateForm } from "../lib/formBuilder/duplicateForm";
export {
  loadAllFormSubmissions as fetchAllFormSubmissions,
  type LoadAllSubmissionsOptions as FetchAllSubmissionsOptions,
} from "../lib/forms/loadAllSubmissions";
